import React, {useCallback, useState} from "react";
import {Box, Card, CardContent, CardMedia, Grid, Tabs, Typography} from "@mui/material";
import ReactECharts from "echarts-for-react";
import PageLayout from "../components/layout/PageLayout";
import ReportFilters from "../components/reports/ReportFilters";
import pieChartPreview from "../assets/reports/pie-chart.svg";
import {StyledTab} from "../components/utils/StyleConstants";
import {useTranslation} from "react-i18next";

const formatPeriodLabel = (period, fallback) => {
    if (!period?.dateFrom || !period?.dateTo) {
        return fallback;
    }
    const formatter = new Intl.DateTimeFormat("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric"
    });
    const fromLabel = formatter.format(new Date(period.dateFrom));
    const toLabel = formatter.format(new Date(period.dateTo));
    return `${fromLabel} - ${toLabel}`;
};

const buildSunburstData = (nodes = [], periodIndex) =>
    nodes.map((node) => {
        const children = buildSunburstData(node.children || [], periodIndex);
        const value = Math.abs(Number(node.values?.[periodIndex] || 0));
        return {
            name: node.name,
            value,
            children: children.length ? children : undefined
        };
    });

const ReportsCategoriesPieChartPage = () => {
    const {t} = useTranslation();
    const [reportType, setReportType] = useState("EXPENSE");
    const [reportResponse, setReportResponse] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const chartOptionForPeriod = useCallback((periodIndex) => {
        const categories = reportResponse?.categories || [];
        if (!categories.length) {
            return null;
        }
        return {
            tooltip: {
                trigger: "item",
                formatter: (params) => {
                    const value = Number(params?.value || 0);
                    const formatted = value.toLocaleString(undefined, {maximumFractionDigits: 2});
                    return `${params?.name || ""}: ${formatted}`;
                }
            },
            series: [
                {
                    type: "sunburst",
                    data: buildSunburstData(categories, periodIndex),
                    radius: [0, "95%"],
                    label: {
                        rotate: "radial"
                    },
                    emphasis: {
                        focus: "ancestor"
                    }
                }
            ]
        };
    }, [reportResponse]);

    const handleApply = useCallback(async (request) => {
        setIsLoading(true);
        setLoadError(null);
        try {
            const response = await fetch("/api/reports/tree", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(request)
            });
            if (!response.ok) {
                throw new Error(`Failed to load report (${response.status})`);
            }
            const json = await response.json();
            setReportResponse(json);
        } catch (error) {
            console.log(error);
            setLoadError(error?.message || t("reports.messages.failedToLoadReport"));
            setReportResponse(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleReset = useCallback(() => {
        setReportResponse(null);
        setLoadError(null);
    }, []);

    return (
        <PageLayout
            title={t("pages.reports.categories.pieChartTitle")}
            tabs={
                <Tabs
                    value={reportType}
                    onChange={(_, newValue) => setReportType(newValue)}
                    aria-label={t("common.labels.reportType")}
                    variant="fullWidth"
                    textColor="inherit"
                    TabIndicatorProps={{
                        style: {
                            color: "white",
                            background: "white"
                        }
                    }}
                >
                    <StyledTab label={t("common.transactionType.expense")} value="EXPENSE" />
                    <StyledTab label={t("common.transactionType.income")} value="INCOME" />
                </Tabs>
            }
        >
            <CardContent sx={{minHeight: 200, maxHeight: "90%", overflowY: "auto"}}>
                <ReportFilters
                    reportType={reportType}
                    onReportTypeChange={setReportType}
                    showTypeTabs={false}
                    onApply={handleApply}
                    onReset={handleReset}
                />
                {isLoading && (
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        {t("reports.messages.loading")}
                    </Typography>
                )}
                {loadError && (
                    <Typography variant="body2" color="error" sx={{mb: 2}}>
                        {loadError}
                    </Typography>
                )}
                {reportResponse && !isLoading && !loadError && (
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        {t("reports.messages.loadedReportWithPeriods", {count: reportResponse.periods?.length || 0})}
                    </Typography>
                )}
                {reportResponse?.periods?.length ? (
                    <Grid container spacing={2}>
                        {reportResponse.periods.map((period, index) => {
                            const option = chartOptionForPeriod(index);
                            const title = formatPeriodLabel(period, `Period ${index + 1}`);
                            return (
                                <Grid item xs={12} md={reportResponse.periods.length > 1 ? 6 : 12} key={`period-${index}`}>
                                    <Card sx={{height: "100%"}}>
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{mb: 1}}>
                                                {title}
                                            </Typography>
                                            {option ? (
                                                <Box sx={{height: 420, width: "100%"}}>
                                                    <ReactECharts option={option} style={{height: "100%", width: "100%"}} />
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("reports.messages.noDataForPeriod")}
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                ) : (
                    <Card sx={{maxWidth: 1000, margin: "0 auto"}}>
                        <CardMedia
                            component="img"
                            image={pieChartPreview}
                            alt={t("pages.reports.categories.cards.pie.alt")}
                        />
                    </Card>
                )}
            </CardContent>
        </PageLayout>
    );
};

export default ReportsCategoriesPieChartPage;
